import { swLog } from 'serviceWorker/helpers/makeSwLogger';
import { LogLevel } from 'shared/types/logger';

export type SwSignalHandler<TPayload> = (payload: TPayload) => unknown;

/**
 * A typed in-process notification of the worker, for one fact several modules
 * react to (logout, a project switch, a queued request). The modules never
 * import each other and the worker never posts on a channel to reach itself.
 */
export interface SwSignal<TPayload = void> {
  readonly name: string;
  emit(...payload: TPayload extends void ? [] : [payload: TPayload]): void;
  /** Returns an idempotent unsubscribe. */
  subscribe(handler: SwSignalHandler<TPayload>): () => void;
}

function reportHandlerError(signalName: string, error: unknown): void {
  swLog(`SW signal handler failed: ${signalName}`, LogLevel.ERROR, error, null);
}

/**
 * Handlers run synchronously in subscription order. A failing handler (also an
 * async one) is logged and does not stop the others, as with event listeners.
 */
export function createSwSignal<TPayload = void>(name: string): SwSignal<TPayload> {
  const handlers = new Set<SwSignalHandler<TPayload>>();

  return {
    name,
    emit(...[payload]) {
      // A copy: handlers may unsubscribe while the signal runs.
      [...handlers].forEach((handler) => {
        try {
          const result = handler(payload as TPayload);
          if (result instanceof Promise) {
            result.catch((error: unknown) => reportHandlerError(name, error));
          }
        } catch (error) {
          reportHandlerError(name, error);
        }
      });
    },
    subscribe(handler) {
      handlers.add(handler);
      return (): void => {
        handlers.delete(handler);
      };
    },
  };
}

/** Subscribes one handler to several payload-less signals; returns one unsubscribe. */
export function subscribeToAnySignal(signals: SwSignal[], handler: () => unknown): () => void {
  const unsubscribes = signals.map((signal) => signal.subscribe(() => handler()));
  return (): void => {
    unsubscribes.forEach((unsubscribe) => unsubscribe());
  };
}
